import { useState } from "react";
import { Link } from "react-router-dom";
import usePageMeta from "@/hooks/usePageMeta";
import { categories } from "@/mocks/resources";

const FORM_URL = "https://readdy.ai/api/form/da1hv58mlpfrg29fpig0";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  name: "关于香蕉动漫",
  description: "香蕉动漫资源聚合站，汇总动漫全集资源与资源站目录，一站找齐你想看的热门动漫合集。",
  inLanguage: "zh-CN",
};

export default function About() {
  const [formStatus, setFormStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  usePageMeta(
    "关于香蕉动漫 - 品牌介绍与联系方式",
    "了解香蕉动漫资源聚合站的品牌定位、发展理念、内容原则与用户口碑，获取关于我们的详细说明与联系方式。",
    "/about",
  );

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = fd.get("website_alt");
    if (honeypot && String(honeypot).trim()) {
      setFormStatus("success");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    setFormStatus("loading");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        body: new URLSearchParams(fd as unknown as Record<string, string>),
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });
      const responseText = await res.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      if (!res.ok || !parsed || parsed?.code !== "OK") {
        setFormStatus("error");
        setFormError(serverMsg || "提交失败，请稍后再试。");
        return;
      }
      setFormStatus("success");
      form.reset();
    } catch {
      setFormStatus("error");
      setFormError("网络异常，请稍后再试。");
    }
  };

  return (
    <main className="pt-24 pb-4">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(aboutSchema) }}
      />

      {/* 首屏 */}
      <section className="bg-secondary-50 border-b border-secondary-200">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-14 md:py-16">
          <nav aria-label="面包屑" className="text-sm text-foreground-600">
            <ol className="flex items-center gap-1 flex-wrap">
              <li><a href="/" className="hover:text-primary-600">首页</a></li>
              <li aria-hidden="true"><i className="ri-arrow-right-s-line"></i></li>
              <li aria-current="page" className="text-foreground-950 font-medium">关于我们</li>
            </ol>
          </nav>
          <div className="mt-8 max-w-2xl">
            <p className="text-xs font-medium text-accent-600 tracking-wide">ABOUT BANANA ANIME</p>
            <h1 className="mt-2 font-heading text-3xl md:text-4xl text-foreground-950">
              关于香蕉动漫
            </h1>
            <p className="mt-4 text-sm md:text-base text-foreground-700 leading-7">
              香蕉动漫是一个专注动漫资源合集与资源站目录整理的聚合平台。我们相信，优秀的动漫值得被轻松找到——无论是补番多年的经典，还是正在热播的新番，都能在这里一站找齐。
            </p>
          </div>
        </div>
      </section>

      {/* 定位与理念 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          <div>
            <h2 className="font-heading text-2xl text-foreground-950">我们的定位</h2>
            <p className="mt-4 text-sm text-foreground-700 leading-7">
              香蕉动漫致力于成为二次元爱好者的「动漫资源导航员」。我们不做简单堆砌，而是以清晰的分类、及时更新的动态和直观的目录结构，帮你在海量内容中快速定位到想看的动漫合集，真正实现追更不迷路。
            </p>
            <div className="mt-6 space-y-3">
              {[
                { icon: "ri-compass-3-line", title: "资源导航", desc: "以聚合目录的方式，串联资源站与合集信息。" },
                { icon: "ri-time-line", title: "及时更新", desc: "连载动态第一时间同步，不错过每一话更新。" },
                { icon: "ri-user-heart-line", title: "用户为本", desc: "围绕找番与补番的真实场景持续优化体验。" },
              ].map((f) => (
                <div key={f.title} className="flex items-start gap-3 bg-background-100/70 rounded-xl p-4">
                  <span className="w-10 h-10 shrink-0 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className={`${f.icon} text-lg`} aria-hidden="true"></i>
                  </span>
                  <div>
                    <h3 className="font-medium text-foreground-950 text-sm">{f.title}</h3>
                    <p className="mt-1 text-sm text-foreground-700">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h2 className="font-heading text-2xl text-foreground-950">发展理念</h2>
            <p className="mt-4 text-sm text-foreground-700 leading-7">
              我们相信，内容与体验同等重要。香蕉动漫从「让找番变简单」出发，坚持把资源整理得更有条理，把更新同步得更及时，让每一位来到这里的朋友都能快速、顺畅地找到喜欢的作品。
            </p>

            <h2 className="mt-8 font-heading text-2xl text-foreground-950">内容原则</h2>
            <ul className="mt-4 space-y-3">
              {[
                "真实整理：资源信息以实际来源为准，标注清晰的整理程度与画质说明。",
                "分类清晰：以观众找番习惯为依据，划分易懂的主题分类与目录结构。",
                "持续维护：对资源信息持续校对与更新，发现错误及时修正。",
                "尊重体验：不虚设功能，保证站点轻量、可读、响应迅速。",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-3 text-sm text-foreground-700 leading-7">
                  <span className="w-6 h-6 shrink-0 rounded-full bg-accent-500 text-background-50 text-xs flex items-center justify-center">
                    {i + 1}
                  </span>
                  {t}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 用户口碑 */}
      <section className="bg-background-100/70 py-14">
        <div className="mx-auto max-w-7xl px-4 md:px-6">
          <div className="text-center">
            <p className="text-xs font-medium text-accent-600 tracking-wide">COMMUNITY</p>
            <h2 className="mt-1 font-heading text-2xl md:text-3xl text-foreground-950">用户这样说</h2>
          </div>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { quote: "分类特别清楚，想补老番直接按主题找，太省心了。", name: "番剧星人", role: "补番爱好者" },
              { quote: "连载动态更新很及时，再也不怕追到一半断更。", name: "追番狂魔", role: "新番追更党" },
              { quote: "画质和剧集说明写得很清楚，找资源不踩坑。", name: "ACG 收藏家", role: "老番收藏者" },
            ].map((t) => (
              <figure key={t.name} className="bg-background-50 border border-background-200 rounded-2xl p-6">
                <div className="flex gap-0.5 text-primary-500">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-sm" aria-hidden="true"></i>
                  ))}
                </div>
                <blockquote className="mt-3 text-sm text-foreground-800 leading-6">「{t.quote}」</blockquote>
                <figcaption className="mt-4 text-sm">
                  <span className="font-medium text-foreground-950">{t.name}</span>
                  <span className="text-foreground-600"> · {t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* 分类一览 */}
      <section className="mx-auto max-w-7xl px-4 md:px-6 py-14">
        <h2 className="font-heading text-2xl text-foreground-950">我们整理的分类</h2>
        <div className="mt-5 flex flex-wrap gap-3">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/library?cat=${c.id}`}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary-100 text-secondary-900 text-sm hover:bg-secondary-200 transition-colors whitespace-nowrap"
            >
              <i className={c.icon} aria-hidden="true"></i>
              {c.name}
              <span className="text-xs">{c.count}</span>
            </Link>
          ))}
        </div>
      </section>

      {/* 联系我们 */}
      <section className="mx-auto max-w-5xl px-4 md:px-6 pb-8">
        <div className="bg-background-100/70 rounded-3xl p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <p className="text-xs font-medium text-accent-600 tracking-wide">CONTACT US</p>
              <h2 className="mt-1 font-heading text-2xl text-foreground-950">联系我们</h2>
              <p className="mt-3 text-sm text-foreground-700 leading-7">
                如果你对香蕉动漫有任何建议、反馈或合作想法，欢迎通过右侧表单与我们联系，我们会尽快回复。
              </p>
              <div className="mt-6 space-y-4 text-sm text-foreground-800">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-primary-100 text-primary-700 flex items-center justify-center">
                    <i className="ri-mail-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">电子邮箱</p>
                    <p className="mt-0.5 font-medium text-foreground-950">hello@banana-anime.example.com</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg bg-accent-100 text-accent-700 flex items-center justify-center">
                    <i className="ri-time-line" aria-hidden="true"></i>
                  </span>
                  <div>
                    <p className="text-xs text-foreground-600">回复时间</p>
                    <p className="mt-0.5 font-medium text-foreground-950">工作日 9:00 - 18:00</p>
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={onSubmit} data-readdy-form id="contact-form" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-950">您的称呼</label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    placeholder="怎么称呼您"
                    className="mt-2 w-full bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-950">电子邮箱</label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className="mt-2 w-full bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="topic" className="block text-sm font-medium text-foreground-950">留言主题</label>
                <input
                  id="topic"
                  name="topic"
                  type="text"
                  placeholder="想聊点什么"
                  className="mt-2 w-full bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-950">留言内容</label>
                <textarea
                  id="message"
                  name="message"
                  required
                  rows={4}
                  maxLength={500}
                  placeholder="请写下您的建议或反馈（500 字以内）"
                  className="mt-2 w-full bg-background-50 border border-background-200 rounded-lg px-4 py-2.5 text-sm outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 resize-none"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={formStatus === "loading"}
                className="w-full px-6 py-3 rounded-full bg-primary-500 text-foreground-950 text-sm font-medium hover:bg-primary-400 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60"
              >
                {formStatus === "loading" ? "提交中..." : "提交留言"}
              </button>

              {formStatus === "success" && (
                <p className="flex items-center gap-2 text-sm text-primary-700">
                  <i className="ri-checkbox-circle-fill" aria-hidden="true"></i>
                  提交成功，感谢您的留言，我们会尽快回复。
                </p>
              )}
              {formStatus === "error" && (
                <p className="flex items-center gap-2 text-sm text-accent-700">
                  <i className="ri-error-warning-fill" aria-hidden="true"></i>
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </main>
  );
}